import { ref, computed, readonly } from 'vue';
import { setAuthState } from './auth';
import { getGracePeriodStatus, clearGracePeriod } from '../api/subscription';

/**
 * Composable for managing payment recovery / grace period state and logic.
 * Extracts business logic from payment-recovery-banner.vue to enable reuse
 * and keep the banner component presentational.
 */
export const usePaymentRecovery = () => {
	const { getUser, isAuthenticated, setUserData } = setAuthState();

	// Reactive state
	const phase = ref('none');
	const daysSinceFailure = ref(0);
	const isDismissed = ref(false);
	const isSoftLockDismissed = ref(false);

	// Computed values
	const daysRemaining = computed(() => {
		if (phase.value === 'grace' || phase.value === 'nuisance') {
			return Math.max(0, 14 - daysSinceFailure.value);
		}
		return 0;
	});

	const shouldShowBanner = computed(() => {
		if (phase.value === 'none') {return false;}
		if (phase.value === 'grace' && isDismissed.value) {return false;}
		if (phase.value === 'soft_lock' && isSoftLockDismissed.value) {return false;}
		return true;
	});

	const canDismiss = computed(() => {
		if (phase.value === 'grace') {
			return !isDismissed.value && daysRemaining.value > 7;
		}
		if (phase.value === 'soft_lock') {return !isSoftLockDismissed.value;}
		return false;
	});

	// Actions
	const dismiss = async () => {
		if (phase.value === 'grace') {
			isDismissed.value = true;
		} else if (phase.value === 'soft_lock') {
			try {
				const response = await clearGracePeriod();
				if (response?.user) {
					setUserData(response.user);
					phase.value = 'none';
					daysSinceFailure.value = 0;
					isSoftLockDismissed.value = true;
				}
			} catch (error) {
				console.error('Failed to clear grace period:', error);
				isSoftLockDismissed.value = true;
			}
		}
	};

	const fetchStatus = async () => {
		try {
			const status = await getGracePeriodStatus();
			if (status) {
				phase.value = status.phase || 'none';
				daysSinceFailure.value = status.days_since_failure || 0;
			}
		} catch (error) {
			console.error('Failed to fetch grace period status:', error);
		}
	};

	const initializeFromUser = () => {
		if (!isAuthenticated()) {return;}

		const user = getUser();
		phase.value = user.grace_period_phase || 'none';

		// Calculate days since failure if payment_failed_at exists
		if (user.payment_failed_at) {
			const failedAt = new Date(user.payment_failed_at);
			const now = new Date();
			const diffTime = Math.abs(now - failedAt);
			daysSinceFailure.value = Math.floor(diffTime / (1000 * 60 * 60 * 24));
		}
	};

	return {
		// State (readonly for external use)
		phase: readonly(phase),
		daysSinceFailure: readonly(daysSinceFailure),
		isDismissed: readonly(isDismissed),

		// Computed
		daysRemaining,
		shouldShowBanner,
		canDismiss,

		// Actions
		dismiss,
		fetchStatus,
		initializeFromUser,
	};
};
